import React, { useState, useEffect, useRef } from 'react';
import { Tooltip } from 'antd';

/**
 * A small SVG ring/donut chart that shows AI token quota usage.
 * Hovering reveals a rich tooltip with exact counts and reset countdown.
 *
 * Props:
 *   quotaInfo  – object from getMyQuota() { daily_quota, used_today, remaining, is_unlimited }
 *   size       – diameter in px (default 36)
 *   strokeWidth – ring thickness (default 3)
 *   showPercent – show % text inside the ring (default true)
 */
const TokenQuotaWidget = ({ quotaInfo, size = 36, strokeWidth = 3, showPercent = true }) => {
  const [, forceUpdate] = useState(0);
  const intervalRef = useRef(null);

  /* Re-render every second to update the countdown */
  useEffect(() => {
    intervalRef.current = setInterval(() => forceUpdate(n => n + 1), 1000);
    return () => clearInterval(intervalRef.current);
  }, []);

  if (!quotaInfo) return null;

  const isUnlimited = quotaInfo.daily_quota === -1 || quotaInfo.is_unlimited;
  const pct = isUnlimited
    ? 100
    : quotaInfo.daily_quota > 0
      ? Math.min(Math.round((quotaInfo.used_today / quotaInfo.daily_quota) * 100), 100)
      : 0;

  /* Color thresholds: green → orange → red */
  const ringColor = isUnlimited
    ? '#52c41a'
    : pct >= 67
      ? '#ff4d4f'
      : pct >= 34
        ? '#faad14'
        : '#52c41a';

  /* SVG geometry */
  const r = (size - strokeWidth * 2) / 2;
  const cx = size / 2;
  const cy = size / 2;
  const circumference = 2 * Math.PI * r;
  const remainingPct = isUnlimited ? 100 : Math.max(100 - pct, 0);
  const stroke = circumference * (remainingPct / 100);
  const gap = circumference - stroke;

  /* Countdown to next midnight UTC */
  const getCountdown = () => {
    const now = new Date();
    const midnight = new Date(now);
    midnight.setUTCHours(24, 0, 0, 0);
    const diffMs = midnight - now;
    if (diffMs <= 0) return '00:00:00';
    const h = Math.floor(diffMs / 3600000);
    const m = Math.floor((diffMs % 3600000) / 60000);
    const s = Math.floor((diffMs % 60000) / 1000);
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  };

  const fmt = (n) => {
    if (n === null || n === undefined || n === -1) return '∞';
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`;
    if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
    return n.toLocaleString();
  };

  /* Rich tooltip content */
  const tooltipContent = (
    <div style={{ minWidth: 160 }}>
      <div style={{
        fontWeight: 700,
        marginBottom: 8,
        fontSize: 13,
        borderBottom: '1px solid rgba(255,255,255,0.15)',
        paddingBottom: 6
      }}>
        AI Token Quota
      </div>

      {isUnlimited ? (
        <div style={{ color: '#52c41a', fontWeight: 600 }}>Unlimited access</div>
      ) : (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
            <span style={{ opacity: 0.8 }}>Used:</span>
            <span style={{ fontWeight: 600, color: ringColor }}>{fmt(quotaInfo.used_today)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
            <span style={{ opacity: 0.8 }}>Remaining:</span>
            <span style={{ fontWeight: 600, color: '#52c41a' }}>
              {fmt(quotaInfo.remaining)}
            </span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
            <span style={{ opacity: 0.8 }}>Daily limit:</span>
            <span style={{ fontWeight: 600 }}>{fmt(quotaInfo.daily_quota)}</span>
          </div>

          {/* Mini progress bar */}
          <div style={{
            height: 6,
            borderRadius: 3,
            background: 'rgba(255,255,255,0.15)',
            margin: '8px 0',
            overflow: 'hidden'
          }}>
            <div style={{
              height: '100%',
              borderRadius: 3,
              width: `${pct}%`,
              background: ringColor,
              transition: 'width 0.4s ease',
            }} />
          </div>
          <div style={{ fontSize: 11, opacity: 0.7, marginBottom: 8 }}>
            {pct}% used today
          </div>
        </>
      )}

      {/* Countdown */}
      <div style={{
        borderTop: '1px solid rgba(255,255,255,0.15)',
        paddingTop: 6,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <span style={{ opacity: 0.7, fontSize: 11 }}>Resets in:</span>
        <span style={{
          fontFamily: 'monospace',
          fontWeight: 700,
          fontSize: 13,
          color: '#69b1ff'
        }}>
          {getCountdown()}
        </span>
      </div>
    </div>
  );

  return (
    <Tooltip
      title={tooltipContent}
      placement="bottomRight"
      overlayInnerStyle={{ padding: '10px 14px', minWidth: 180 }}
    >
      <div style={{ cursor: 'default', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
        <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
          {/* Background track */}
          <circle cx={cx} cy={cy} r={r} fill="none" stroke="rgba(128,128,128,0.2)" strokeWidth={strokeWidth} />
          {/* Remaining arc */}
          <circle
            cx={cx} cy={cy} r={r} fill="none"
            stroke={ringColor} strokeWidth={strokeWidth} strokeLinecap="round"
            strokeDasharray={`${stroke} ${gap}`}
            style={{ transition: 'stroke-dasharray 0.4s ease, stroke 0.4s ease' }}
          />
          {/* Percentage text */}
          {showPercent && (
            <text
              x={cx} y={cy} textAnchor="middle" dominantBaseline="central"
              style={{
                transform: `rotate(90deg)`,
                transformOrigin: `${cx}px ${cy}px`,
                fontSize: isUnlimited ? size * 0.52 : size * 0.27,
                fontWeight: 700,
                fill: ringColor,
                fontFamily: 'sans-serif',
              }}
            >
              {isUnlimited ? '∞' : `${100 - pct}%`}
            </text>
          )}
        </svg>
      </div>
    </Tooltip>
  );
};

export default TokenQuotaWidget;
